<template>
  <DetailLayoutPage
    :title="title"
    :subtitle="teaserText"
    :lead="lead"
    :body="bodyText"
    :is-sustainable="isSustainable"
    :nid-nearby="nid"
    :nid-related="nid"
    :rating="rating"
    :features="features"
    large-features
    :address
    :nid="nid"
    :key-visual="keyVisual"
    :images="mediaSlider"
  >
    <template v-if="address" #address>
      <AddressCard :address :title="title" :nid="nid" />
    </template>

    <template v-if="stcId && nid" #cta>
      <AccomodationBooking :nid="nid" :stc-id="stcId" />
    </template>

    <template v-if="nid" #below>
      <TeaserQueryNearby :nid="nid" />
    </template>
  </DetailLayoutPage>
</template>

<script lang="ts" setup>
import type { NuxtSvgSpriteSymbol } from '#nuxt-svg-icon-sprite/runtime'
import type { NodeAccomodationFragment } from '#graphql-operations'
import type { FeatureItem } from '~/components/Features/index.vue'
import { getCantoImageStyle } from '~/helpers/canto'

const HOTEL_FEATURE_ICONS: Record<string, NuxtSvgSpriteSymbol> = {
  '522': 'air_conditioning', // Air conditioning
  '523': 'elevator', // Elevators
  '524': 'gym', // Exercise gym
  '527': 'hot-tub', // Steam Bathing
  '528': 'indoor_parking', // Indoor parking
  '529': 'indoor_parking', // Outdoor parking
  '530': 'pool', // Pool
  '531': 'restaurant', // Restaurant
  '532': 'sauna', // Sauna
  '533': 'playground', // Children's playground
  '535': 'electric-car', // Charging station electric cars
  '539': 'wifi', // Free high speed internet connection
  '540': 'pets_allowed', // Pets allowed
  '543': 'non_smoking_rooms', // Non-smoking rooms
}

const props = defineProps<NodeAccomodationFragment>()

provide('currentNodeId', props.nid)

const features = computed<FeatureItem[]>(() => {
  return (
    props.hotelFeatures?.map((term) => {
      const icon: NuxtSvgSpriteSymbol = term?.tid
        ? HOTEL_FEATURE_ICONS[term.tid]!
        : 'check'
      return {
        text: term?.name || '',
        icon,
      }
    }) || []
  )
})

const bodyText = computed(() => props.body)

const { amenityFeatures: accessibilityFeatures } = await useAccessibilityInfo(
  () => props.nid,
)

const schemaOrg = computed(() => {
  const amenityFeatures = [
    ...features.value.map((feature) => ({
      '@type': 'LocationFeatureSpecification',
      name: feature.text,
      value: true,
    })),
    ...accessibilityFeatures.value,
  ]

  const drupalImage =
    props.keyVisual && 'image' in props.keyVisual
      ? props.keyVisual.fileUrl
      : undefined
  const cantoImage =
    props.keyVisual && 'assetId' in props.keyVisual
      ? getCantoImageStyle(props.keyVisual.assetId!, {
          width: 1280,
          height: 960,
        })
      : undefined

  const address = props.address
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Hotel',
        name: props.title,
        image: drupalImage?.path || cantoImage?.url || undefined,
        url: address?.website?.url?.path || undefined,
        email: address?.email || undefined,
        telephone: address?.phone || undefined,
        address: {
          '@type': 'PostalAddress',
          description: address?.address?.organization || undefined,
          streetAddress: address?.address?.addressLine1 || undefined,
          postalCode: address?.address?.postalCode || undefined,
          addressLocality: address?.address?.locality || undefined,
          addressRegion: address?.address?.region || undefined,
          addressCountry: {
            '@type': 'Country',
            name: address?.address?.countryCode || undefined,
          },
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: address?.location?.lat || undefined,
          longitude: address?.location?.lon || undefined,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: props.rating?.stars,
          reviewCount: 1, // Stars are a fixed value in discover.swiss: https://track.liip.ch/issue/ZT-1579
        },
        amenityFeature: amenityFeatures,
      },
    ],
  }
})

useHead(
  computed(() => ({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schemaOrg.value),
      },
    ],
  })),
)
</script>
